/**
 * Utility functions for formatting dates in a human-readable way
 */

export const formatDateTime = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    // Format as: "Sep 14, 2025 at 10:30 PM"
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
    return `${dateStr} at ${timeStr}`
  } catch (error) {
    console.error('Error formatting date:', error)
    return 'Invalid Date'
  }
}

export const formatDateTimeWithTimezone = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }

    // Format as: "Sep 14, 2025 at 10:30 PM EST"
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    })
    return `${dateStr} at ${timeStr}`
  } catch (error) {
    console.error('Error formatting date with timezone:', error)
    return 'Invalid Date'
  }
}

export const formatRelativeTime = (dateString: string): string => {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMinutes < 1) return 'Just now'
    if (diffMinutes < 60) return `${diffMinutes} min ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

    // For older dates, show full date
    return formatDateTime(dateString)
  } catch (error) {
    console.error('Error formatting relative time:', error)
    return 'Invalid Date'
  }
}